import type { Contact, Recruiter } from '../services/api'
import { ensureHttpProtocol } from './validation'

// MWA-310 — the outreach composer was written against a networking Contact,
// but a search-partner intro is the same act aimed at a Recruiter: pick a
// template, resolve the merge fields, review, send. Rather than a second
// composer that has to be kept in step, both sides adapt to this shape and
// the composer reads nothing else about who it is writing to.
export interface OutreachRecipient {
  id: string
  /** How the message greets them. */
  displayName: string
  /** Their employer — a contact's company, a recruiter's firm. */
  company: string
  email: string
  /** Empty when unknown; the composer hides the channels it cannot address. */
  phone: string
  /**
   * Always carries a scheme. A stored value like "www.linkedin.com/in/x" is a
   * relative path to the browser, so an href built from it resolves against
   * the app origin and navigates inside the SPA. Normalized here rather than
   * at each render site, so no future caller can miss it.
   */
  linkedinUrl: string
  /** Who introduced them, for the {sharedConnection} merge field. */
  referredBy: string
  /**
   * Which outreach stage the recipient sits at, for templates that declare
   * `assignedStages`. Null means the recipient is not staged at all and every
   * template in the category applies.
   */
  stage: string | null
}

export function recipientFromContact(contact: Contact): OutreachRecipient {
  return {
    id: contact.id,
    // Greeting by first name is the point; the full name is the fallback for
    // an imported row that never had one.
    displayName: contact.firstName || contact.fullName || '',
    company: contact.company || '',
    email: contact.email || '',
    phone: contact.phone || '',
    linkedinUrl: ensureHttpProtocol(contact.linkedinUrl),
    referredBy: contact.referredBy || '',
    stage: contact.status ?? null,
  }
}

export function recipientFromRecruiter(recruiter: Recruiter): OutreachRecipient {
  return {
    id: recruiter.id,
    displayName: recruiter.name || '',
    company: recruiter.firm || '',
    email: recruiter.email || '',
    // Recruiter has no phone column. Empty rather than undefined, so the
    // composer's "can I text this person" check is a plain falsy test and no
    // sms: link is ever built from nothing.
    phone: '',
    linkedinUrl: ensureHttpProtocol(recruiter.linkedinUrl),
    // Nobody refers you to a search partner — they are found by research, so
    // {sharedConnection} falls back to its own default rather than breaking.
    referredBy: '',
    // Search partners are not moved through the networking outreach stages.
    stage: null,
  }
}
